import { useId } from "react";
import { buildFormattedLessonNote, type LocalLessonNoteDraft } from "../lessonNoteFormatting.js";
import { Drawer } from "../ui/Dialog.js";

interface Props {
  sourceContent: string;
  draft: LocalLessonNoteDraft;
  onDraftChange: (draft: LocalLessonNoteDraft) => void;
  onApplySummary: (content: string) => void;
  onCreatePracticeTask: (suggestion: string) => void;
  onClose: () => void;
  disabled?: boolean;
}

export function LocalLessonNoteFormatter({
  sourceContent,
  draft,
  onDraftChange,
  onApplySummary,
  onCreatePracticeTask,
  onClose,
  disabled = false,
}: Props) {
  const id = useId();
  const formattedNote = buildFormattedLessonNote(draft);

  function updateSummary(value: string) {
    onDraftChange({
      ...draft,
      summaryItems: value.split(/\r?\n/).map((line) => line.trim()),
    });
  }

  function updateSuggestion(index: number, value: string) {
    onDraftChange({
      ...draft,
      practiceSuggestions: draft.practiceSuggestions.map((item, itemIndex) => itemIndex === index ? value : item),
    });
  }

  return (
    <Drawer
      title="Format lesson notes"
      eyebrow="Local template"
      description="Review and edit the proposed summary and practice suggestions before using them."
      onClose={onClose}
      size="large"
      busy={disabled}
    >
      <div className="stack">
        <p className="small muted" role="status">
          Formatting runs in this page with local rules. No AI model or external service receives the note.
          Your lesson note stays unchanged until you use the formatted draft; saving or publishing is a separate action.
        </p>
        <details>
          <summary className="small">Original lesson note</summary>
          <pre className="lesson-notes-recovery-preview">{sourceContent}</pre>
        </details>

        <label htmlFor={`local-note-summary-${id}`}>
          <span>Lesson summary</span>
          <textarea
            id={`local-note-summary-${id}`}
            rows={Math.max(4, Math.min(10, draft.summaryItems.length))}
            value={draft.summaryItems.join("\n")}
            disabled={disabled}
            onChange={(event) => updateSummary(event.target.value)}
          />
        </label>

        <section className="stack" aria-labelledby={`local-note-practice-heading-${id}`}>
          <h3 id={`local-note-practice-heading-${id}`}>Suggested practice</h3>
          <p className="small muted">Preparing a task applies this reviewed draft to the lesson note first. Save the lesson draft before creating the student task.</p>
          {draft.practiceSuggestions.length === 0 && (
            <p className="small muted">No explicit practice instructions were found. You can add one manually; nothing is suggested automatically.</p>
          )}
          {draft.practiceSuggestions.map((suggestion, index) => (
            <div className="panel stack" key={index}>
              <label htmlFor={`local-note-practice-${id}-${index}`}>Practice suggestion {index + 1}</label>
              <textarea
                id={`local-note-practice-${id}-${index}`}
                rows={2}
                value={suggestion}
                disabled={disabled}
                onChange={(event) => updateSuggestion(index, event.target.value)}
              />
              <div className="row">
                <button type="button" className="quiet" disabled={disabled || !suggestion.trim()} onClick={() => onCreatePracticeTask(suggestion.trim())}>
                  Prepare practice task
                </button>
                <button
                  type="button"
                  className="quiet"
                  disabled={disabled}
                  onClick={() => onDraftChange({ ...draft, practiceSuggestions: draft.practiceSuggestions.filter((_, itemIndex) => itemIndex !== index) })}
                >
                  Remove suggestion
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            className="quiet"
            disabled={disabled}
            onClick={() => onDraftChange({ ...draft, practiceSuggestions: [...draft.practiceSuggestions, ""] })}
          >
            Add practice suggestion
          </button>
        </section>

        <div className="row">
          <button type="button" disabled={disabled || !formattedNote.trim()} onClick={() => onApplySummary(formattedNote)}>
            Use formatted note
          </button>
          <button type="button" className="quiet" disabled={disabled} onClick={onClose}>Close</button>
        </div>
      </div>
    </Drawer>
  );
}
